/**
 * Trailing-edge debounce.
 *
 * Returns a function that, when invoked repeatedly, only calls `fn` after
 * `delay` milliseconds have passed since the last invocation. The returned
 * function exposes a `.cancel()` method that drops any pending invocation
 * (used during cleanup so a queued call can't fire after teardown).
 *
 * Used to wrap `window.addEventListener('resize', ...)` in
 * `core/initializer.js` so a resize-drag stops triggering ~60Hz of
 * section-iteration work. The trade-off — a small visual lag (delay ms)
 * before swipers re-evaluate after the user stops resizing — is invisible
 * at the chosen delay.
 *
 * @template {(...args: any[]) => void} F
 * @param {F} fn
 * @param {number} delay  Milliseconds to wait after the last call.
 * @returns {F & { cancel: () => void }}
 */
export function debounce(fn, delay) {
  let timer = null;
  function debounced(...args) {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      fn.apply(this, args);
    }, delay);
  }
  debounced.cancel = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };
  return debounced;
}

export default debounce;
